import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error | timeout
  const [orderId, setOrderId] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderId(data.order_id);
        if (data.guest_token) setGuestToken(data.guest_token);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const orderPath = orderId ? (guestToken ? `/orders/${orderId}?g=${guestToken}` : `/orders/${orderId}`) : "/dashboard";

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-background px-6 py-16" data-testid="checkout-success-page">
      <div className="w-full max-w-md rounded-3xl border border-border bg-white p-10 text-center shadow-[0_8px_30px_rgba(10,61,98,0.06)]">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-teal" />
            <h1 className="mt-6 text-2xl font-bold text-ocean">Confirming your payment…</h1>
            <p className="mt-2 text-slate-600">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" data-testid="checkout-success-icon" />
            <h1 className="mt-6 text-2xl font-bold text-ocean">Payment confirmed!</h1>
            <p className="mt-2 text-slate-600">Your order is in. Print your prepaid shipping label and track everything from here{guestToken ? " — no login needed" : ""}.</p>
            <button onClick={() => navigate(orderPath)} data-testid="checkout-success-continue"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-ocean px-7 py-3 text-sm font-semibold text-white hover:bg-ocean-light">
              View my order & print label <ArrowRight className="h-4 w-4" />
            </button>
            {guestToken && (
              <p className="mt-4 text-xs text-slate-400">We've emailed your label link and will send results there too. Want all your tests in one place? <Link to="/register" className="font-semibold text-teal-dark">Create a free account</Link>.</p>
            )}
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-amber-500" />
            <h1 className="mt-6 text-2xl font-bold text-ocean">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
            <p className="mt-2 text-slate-600">{state === "timeout" ? "Your payment is taking longer than usual. Check your dashboard shortly." : "We couldn't confirm this payment. If you were charged, it will appear in your dashboard."}</p>
            <Link to={orderId ? orderPath : "/dashboard"} className="mt-7 inline-flex items-center gap-2 rounded-full bg-ocean px-7 py-3 text-sm font-semibold text-white hover:bg-ocean-light">{orderId ? "View my order" : "Go to dashboard"} <ArrowRight className="h-4 w-4" /></Link>
          </>
        )}
      </div>
    </div>
  );
}
